import { useCallback, useMemo } from 'react';
import { analyticsService } from '@/services/analytics/firebase-analytics';
import {
  firestoreService,
  ResumeRequest,
} from '@/services/analytics/firebase-firestore';
import { Timestamp } from 'firebase/firestore';
import { logDebug, logError, logWarn } from '@personal-portfolio/common-utils';

export const useFirebase = () => {
  const trackPageView = useCallback(async (page: string, title?: string) => {
    try {
      logDebug(`Tracking page: ${page} (${title})`);

      analyticsService.trackPageView(page, title);
    } catch (error) {
      logWarn('Failed to track page view:', error);
    }
  }, []);

  const trackEvent = useCallback(
    async (
      eventName: string,
      parameters?: Record<string, string | number | boolean | undefined>,
    ) => {
      try {
        await analyticsService.trackEvent({
          event_name: eventName,
          event_parameters: parameters,
        });
      } catch (error) {
        logWarn('Failed to track event:', error);
      }
    },
    [],
  );

  // Convenience methods
  const trackResumeDownload = useCallback(() => {
    trackEvent('resume_download', {
      file_name: 'resume.pdf',
      download_source: window.location.pathname,
    });
  }, [trackEvent]);

  const trackCertificateView = useCallback(
    (awardTitle: string) => {
      trackEvent('certificate_view', {
        award_title: awardTitle,
        content_type: 'certificate',
      });
    },
    [trackEvent],
  );

  const trackCertificateDownload = useCallback(
    (awardTitle: string) => {
      trackEvent('certificate_download', {
        award_title: awardTitle,
        content_type: 'certificate',
      });
    },
    [trackEvent],
  );

  const trackExternalLinkClick = useCallback(
    (url: string, text: string, section: string) => {
      trackEvent('external_link_click', {
        link_url: url,
        link_text: text,
        section,
        source_page: window.location.pathname,
      });
    },
    [trackEvent],
  );

  const trackContactFormSubmit = useCallback(
    (method: string) => {
      trackEvent('contact_form_submit', {
        contact_method: method,
        form_page: window.location.pathname,
      });
    },
    [trackEvent],
  );

  const trackThemeToggle = useCallback(
    (theme: 'light' | 'dark') => {
      trackEvent('theme_toggle', {
        theme,
        previous_theme: theme === 'light' ? 'dark' : 'light',
      });
    },
    [trackEvent],
  );

  // Resume Request creation (the browser only CREATES resume_requests;
  // reads/status updates are server-side only — see firebase-firestore.ts)
  const saveResumeRequest = useCallback(
    async (
      requestData: Omit<
        ResumeRequest,
        'id' | 'timestamp' | 'sessionId' | 'status'
      >,
    ): Promise<string> => {
      try {
        // Filter out undefined values to prevent Firestore errors
        const cleanRequestData = Object.fromEntries(
          Object.entries(requestData).filter(
            ([, value]) => value !== undefined,
          ),
        ) as Omit<ResumeRequest, 'id' | 'timestamp' | 'sessionId' | 'status'>;

        const resumeRequest: Omit<ResumeRequest, 'id'> = {
          ...cleanRequestData,
          timestamp: Timestamp.now(),
          status: 'pending',
        };

        const requestId =
          await firestoreService.saveResumeRequest(resumeRequest);

        // Note: Analytics tracking moved to the component level for cleaner flow
        return requestId;
      } catch (error) {
        logError('Failed to save resume request:', error);
        throw error;
      }
    },
    [],
  );

  // Memoized so consumers get stable identities across renders (safe to use
  // in dependency arrays without re-triggering effects).
  return useMemo(
    () => ({
      trackPageView,
      trackEvent,
      trackResumeDownload,
      trackCertificateView,
      trackCertificateDownload,
      trackExternalLinkClick,
      trackContactFormSubmit,
      trackThemeToggle,
      // Resume Request methods
      saveResumeRequest,
    }),
    [
      trackPageView,
      trackEvent,
      trackResumeDownload,
      trackCertificateView,
      trackCertificateDownload,
      trackExternalLinkClick,
      trackContactFormSubmit,
      trackThemeToggle,
      saveResumeRequest,
    ],
  );
};
